/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

export enum DocumentVersionLibraryFlag {
  providence_only_library = 'providence_only_library', // Whether the document library is the providence only library
  musc_only_library = 'musc_only_library', // Whether the document library is the MUSC only library
  demo_library = 'demo_library', // Whether the document library is the demo only library
  demorg_library = 'demorg_library', // Whether the document library is the demo org library
}

/** Make an enum of flags
 *
 *  When adding, please also update:
 *   - IconForFlag (to return the new icon for the flag)
 *   - FlagsMetadataType (to set whether it can be toggled by the user)
 */
export const DocumentVersionFlag = {
  contextualized: 'contextualized',
  internal_only: 'internal_only',
  star: 'star',
  new: 'new',
  contextualized_forced: 'contextualized_forced',
  magnifying_glass: 'magnifying_glass',
  has_comments_from_ai: 'has_comments_from_ai',
  has_comments_needing_attention: 'has_comments_needing_attention',
  deprecated: 'deprecated',
  red_flag: 'redflag',
  yellow_flag: 'yellowflag',
  tracked_changes: 'tracked_changes',
  toc: 'toc',
  ...DocumentVersionLibraryFlag,
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DocumentVersionFlag = (typeof DocumentVersionFlag)[keyof typeof DocumentVersionFlag];

type FlagsMetadataType = {
  name: DocumentVersionFlag;
  friendlyName: string;
  negativeFriendlyName?: string;
  isStatus?: boolean;
  canToggle?: boolean;
  large?: boolean;
  visibleInConsumer: boolean;
};

export const flagsMetadata: readonly FlagsMetadataType[] = [
  {
    name: DocumentVersionFlag.contextualized,
    friendlyName: 'Contextualized',
    negativeFriendlyName: 'Not Contextualized',
    isStatus: true,
    canToggle: false,
    visibleInConsumer: true,
  },
  {
    name: DocumentVersionFlag.contextualized_forced,
    friendlyName: 'Force Contextualized',
    canToggle: true,
    visibleInConsumer: true,
  }, // For a user to mark as contextualized
  { name: DocumentVersionFlag.internal_only, friendlyName: 'Internal Only', canToggle: true, visibleInConsumer: false },
  {
    name: DocumentVersionFlag.magnifying_glass,
    friendlyName: 'Needs Review',
    canToggle: true,
    visibleInConsumer: false,
  },
  { name: DocumentVersionFlag.new, friendlyName: 'New', canToggle: true, large: true, visibleInConsumer: false },
  {
    name: DocumentVersionFlag.providence_only_library,
    friendlyName: 'Providence Library Only',
    canToggle: false,
    visibleInConsumer: false,
  }, // document is in a Providence only library
  {
    name: DocumentVersionFlag.musc_only_library,
    friendlyName: 'MUSC Library Only',
    canToggle: false,
    visibleInConsumer: false,
  },
  {
    name: DocumentVersionFlag.demo_library,
    friendlyName: 'Demo Library Only',
    canToggle: false,
    visibleInConsumer: false,
  },
  {
    name: DocumentVersionFlag.demorg_library,
    friendlyName: 'Demo Org Library Only',
    canToggle: false,
    visibleInConsumer: false,
  },
  { name: DocumentVersionFlag.star, friendlyName: 'Starred', canToggle: true, visibleInConsumer: false }, // disable for now
  {
    name: DocumentVersionFlag.deprecated,
    friendlyName: 'Deprecated',
    canToggle: true,
    visibleInConsumer: false,
  }, // mark document as deprecated - will be archived in the future
  { name: DocumentVersionFlag.red_flag, friendlyName: 'Red Flag', canToggle: true, visibleInConsumer: false },
  { name: DocumentVersionFlag.yellow_flag, friendlyName: 'Yellow Flag', canToggle: true, visibleInConsumer: false },
  {
    name: DocumentVersionFlag.tracked_changes,
    friendlyName: 'Has Unresolved Track Changes',
    negativeFriendlyName: 'No unresolved track changes on any page',
    isStatus: true,
    canToggle: false,
    visibleInConsumer: false,
  }, // whether the document has any ckeditor unresolved tracked changes on it.
  {
    name: DocumentVersionFlag.has_comments_needing_attention,
    friendlyName: 'Comments Needing Attention',
    canToggle: false,
    visibleInConsumer: false,
  },
  { name: DocumentVersionFlag.toc, friendlyName: 'Table of Contents', canToggle: true, visibleInConsumer: false },
] as const;

export function isDocumentVersionInternalOnly(documentVersion?: { flags?: string[] }) {
  return !!documentVersion?.flags?.includes(DocumentVersionFlag.internal_only);
}

export function getDocumentVersionPublicationBlockReason(documentVersion?: { flags?: string[] }) {
  if (isDocumentVersionInternalOnly(documentVersion)) {
    return 'Internal-only documents cannot be published.';
  }
  return undefined;
}
